import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { STATUS_META, monthOptions, monthLabel } from "@/lib/dpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ProratedBadge } from "@/components/PlanBits";
import { AlertTriangle } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";

export default function DpcPlans() {
  const { user } = useApp();
  const [month, setMonth] = useState("");
  const [board, setBoard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/dpc/meta").then(({ data }) => { setMeta(data); setMonth((m) => m || data.month); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!month) return;
    api.get(`/dpc/plans?${qs({ month })}`).then(({ data }) => setBoard(data)).catch((e) => toast.error(apiError(e)));
  }, [month]);

  const daysLeft = meta?.plan_window?.due ? Math.ceil((new Date(meta.plan_window.due) - new Date()) / 86400000) : null;

  const bulkUpload = async () => {
    if (!bulkFile) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      fd.append("month", month);
      const { data } = await api.post("/dpc/plan/bulk", fd);
      toast.success(`Plans created for: ${data.created.join(", ") || "none"}${data.skipped_locked.length ? ` · skipped (locked): ${data.skipped_locked.join(", ")}` : ""}`);
      setBulkFile(null);
      api.get(`/dpc/plans?${qs({ month })}`).then(({ data }) => setBoard(data));
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  if (!board) return <div className="text-sm text-muted-foreground py-20 text-center">Loading plan board…</div>;
  const counts = board.rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

  return (
    <div className="space-y-4" data-testid="dpc-plans">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">DPC Plans <span className="text-muted-foreground font-medium text-base">· {monthLabel(month)}</span></h1>
          {meta && month === meta.month && daysLeft != null && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="dpc-plan-countdown">
              Submission due {meta.plan_window.due}{daysLeft >= 0 ? ` — ${daysLeft} day(s) left` : " — overdue"}
              {daysLeft < 0 && user?.role === "vp" && user?.department === "sales" && <span className="text-amber-700" data-testid="dpc-vp-late-hint"> · window closed: as VP Sales you can open and revise any plan; Admin &amp; MD are notified</span>}
            </p>
          )}
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="dpc-plans-month"><SelectValue /></SelectTrigger>
          <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <span key={k} className={`rounded px-2 py-1 font-medium ${v.cls}`} data-testid={`dpc-status-count-${k}`}>{v.label}: {counts[k] || 0}</span>
        ))}
      </div>

      <div className="border bg-card rounded-md overflow-auto max-h-[62vh]">
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card"><tr>{["Dealer", "District", "Status", "Ver", "Retail plan", "Week shape", "Submitted", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {board.rows.map((r) => {
              const s = STATUS_META[r.status] || STATUS_META.not_started;
              return (
                <tr key={r.dealer_code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`dpc-plan-row-${r.dealer_code}`}>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{r.dealer_code} <span className="font-normal text-muted-foreground">{r.dealer_name}</span></td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.district}</td>
                  <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${s.cls}`}>{s.label}</span></td>
                  <td className="px-3 py-1.5 num text-xs">{r.version || "—"}</td>
                  <td className="px-3 py-1.5 num font-bold">{r.retail_plan}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {r.back_loaded && <span className="inline-flex items-center gap-1 rounded bg-amber-400 text-amber-950 px-1.5 py-0.5 text-[10px] font-bold" title={`A week carries more than ${board.week_share_threshold}% of the month, or W4 is 50%+ above the W1–W3 average — approvers must leave a comment`} data-testid={`dpc-plan-backloaded-${r.dealer_code}`}><AlertTriangle size={10} /> Back-loaded</span>}
                      <ProratedBadge count={r.prorated ? 1 : 0} testId={`dpc-plan-prorated-${r.dealer_code}`} />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.submitted_at ? r.submitted_at.slice(0, 10) : "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Link to={`/dpc/plan/${r.dealer_code}?month=${month}`} className="rounded border px-2 py-1 text-xs hover:bg-secondary transition-colors duration-200" data-testid={`dpc-open-plan-${r.dealer_code}`}>Open</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {user?.role === "admin" && (
        <div className="border bg-card rounded-md p-4 flex flex-wrap items-center gap-3" data-testid="dpc-bulk-upload">
          <div className="text-sm font-semibold font-heading">Bulk plan upload</div>
          <input type="file" accept=".xlsx" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} className="text-xs" data-testid="dpc-bulk-file" />
          <Button size="sm" onClick={bulkUpload} disabled={!bulkFile || bulkBusy} data-testid="dpc-bulk-btn">{bulkBusy ? "Uploading…" : `Upload for ${monthLabel(month)}`}</Button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/dpc/plan-template`} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="dpc-template-link">Download template</a>
          <span className="text-[11px] text-muted-foreground">Long format: dealer_code | model | source | week | stage | plan_qty — creates plans as Submitted.</span>
        </div>
      )}
    </div>
  );
}
